'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
interface PlaceholdersAndVanishInputProps {
  placeholders: string[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isSuccess?: boolean;
  onAnimationComplete?: () => void;
}
export function PlaceholdersAndVanishInput(props: PlaceholdersAndVanishInputProps) {
  const input = usePlaceholderVanishController({
    ...props
  });
  return <form className={cn('relative mx-auto h-12 w-full max-w-xl overflow-hidden rounded-full bg-white shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),_0px_1px_0px_0px_rgba(25,28,33,0.02),_0px_0px_0px_1px_rgba(25,28,33,0.08)] transition duration-200 dark:bg-zinc-800', input.value && 'bg-neutral-50')} onSubmit={input.handleSubmit}>
      <canvas className={cn('pointer-events-none absolute top-[20%] left-2 origin-top-left scale-50 transform pr-20 text-base invert filter sm:left-8 dark:invert-0', !input.animating ? 'opacity-0' : 'opacity-100')} ref={input.canvasRef} />
      <input onChange={input.handleChange} onKeyDown={input.handleKeyDown} ref={input.inputRef} value={input.value} type="text" aria-label="Email address" className={cn('relative z-50 size-full rounded-full border-none bg-transparent pr-20 pl-4 text-sm text-black focus:ring-0 focus:outline-none sm:pl-4 sm:text-base dark:text-white', input.animating && 'text-transparent dark:text-transparent')} />
      <SubmitButton input={input} isSuccess={props.isSuccess ?? false} />
      <PlaceholderText input={input} placeholders={props.placeholders} />
    </form>;
}
function usePlaceholderVanishController({
  placeholders,
  onChange,
  onSubmit,
  onAnimationComplete
}: PlaceholdersAndVanishInputProps) {
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  useEffect(() => {
    const advancePlaceholder = () => {
      setCurrentPlaceholder(prev => (prev + 1) % placeholders.length);
    };
    let interval = setInterval(advancePlaceholder, 3000);
    const handleVisibilityChange = () => {
      clearInterval(interval);
      if (document.visibilityState === 'visible') {
        interval = setInterval(advancePlaceholder, 3000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [placeholders]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const newDataRef = useRef<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [animating, setAnimating] = useState(false);
  const draw = () => {
    if (!inputRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = 800;
    canvas.height = 800;
    ctx.clearRect(0, 0, 800, 800);
    const computedStyles = getComputedStyle(inputRef.current);
    const fontSize = parseFloat(computedStyles.getPropertyValue('font-size'));
    ctx.font = `${fontSize * 2}px ${computedStyles.fontFamily}`;
    ctx.fillStyle = '#FFF';
    ctx.fillText(value, 16, 40);
    const imageData = ctx.getImageData(0, 0, 800, 800);
    const pixelData = imageData.data;
    const newData: any[] = [];
    for (let t = 0; t < 800; t++) {
      const i = 4 * t * 800;
      for (let n = 0; n < 800; n++) {
        const e = i + 4 * n;
        if (pixelData[e] !== 0 && pixelData[e + 1] !== 0 && pixelData[e + 2] !== 0) {
          newData.push({
            x: n,
            y: t,
            color: [pixelData[e], pixelData[e + 1], pixelData[e + 2], pixelData[e + 3]]
          });
        }
      }
    }
    newDataRef.current = newData.map(({
      x,
      y,
      color
    }) => ({
      x,
      y,
      r: 1,
      color: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
    }));
  };
  useEffect(() => {
    // Debounce the expensive draw operation
    if (!value) {
      // Clear immediately when empty
      draw();
      return;
    }
    const timeoutId = setTimeout(() => {
      draw();
    }, 100);
    return () => clearTimeout(timeoutId);
    // `value` is the real trigger; draw is rebuilt each render (compiler-stable)
    // and intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const animate = (start: number) => {
    const animateFrame = (pos: number = 0) => {
      requestAnimationFrame(() => {
        const newArr = [];
        for (let i = 0; i < newDataRef.current.length; i++) {
          const current = newDataRef.current[i];
          if (current.x < pos) {
            newArr.push(current);
          } else {
            if (current.r <= 0) {
              current.r = 0;
              continue;
            }
            current.x += Math.random() > 0.5 ? 1 : -1;
            current.y += Math.random() > 0.5 ? 1 : -1;
            current.r -= 0.05 * Math.random();
            newArr.push(current);
          }
        }
        newDataRef.current = newArr;
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          ctx.clearRect(pos, 0, 800, 800);
          newDataRef.current.forEach(t => {
            const {
              x: n,
              y: i,
              r: s,
              color: color
            } = t;
            if (n > pos) {
              ctx.beginPath();
              ctx.rect(n, i, s, s);
              ctx.fillStyle = color;
              ctx.strokeStyle = color;
              ctx.stroke();
            }
          });
        }
        if (newDataRef.current.length > 0) {
          animateFrame(pos - 6); // Adjusted speed
        } else {
          setValue('');
          setAnimating(false);
          // Trigger success callback if provided
          if (onAnimationComplete) {
            onAnimationComplete();
          }
        }
      });
    };
    animateFrame(Number(start));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !animating) {
      vanishAndSubmit();
    }
  };
  const vanishAndSubmit = () => {
    setAnimating(true);
    draw();
    const value = inputRef.current?.value || '';
    if (value && inputRef.current) {
      const maxX = newDataRef.current.reduce((prev, current) => current.x > prev ? current.x : prev, 0);
      animate(maxX);
    } else {
      // If no value, still trigger animation complete
      setAnimating(false);
      if (onAnimationComplete) {
        setTimeout(onAnimationComplete, 300); // Small delay for visual feedback
      }
    }
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    vanishAndSubmit();
    if (onSubmit) {
      onSubmit(e);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!animating) {
      setValue(e.target.value);
      if (onChange) {
        onChange(e);
      }
    }
  };
  return {
    canvasRef,
    inputRef,
    value,
    animating,
    currentPlaceholder,
    handleSubmit,
    handleChange,
    handleKeyDown
  };
}
type PlaceholderVanishState = ReturnType<typeof usePlaceholderVanishController>;
function SubmitButton(t0) {
  const $ = _c(11);
  const {
    input,
    isSuccess
  } = t0;
  const t1 = !input.value && !isSuccess;
  const t2 = isSuccess ? "Subscribed successfully" : "Subscribe";
  const t3 = isSuccess ? "bg-zinc-800 dark:bg-zinc-700" : "bg-neutral-950 dark:bg-zinc-900";
  const t4 = !input.value && !isSuccess && "bg-neutral-100 dark:bg-zinc-800";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = cn("absolute top-1/2 right-2 z-50 flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition duration-200", t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== input.value || $[4] !== isSuccess) {
    t6 = <AnimatePresence mode="wait">{isSuccess ? <m.svg key="checkmark" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" initial={{
        scale: 0.95,
        opacity: 0
      }} animate={{
        scale: [0.8, 1.1, 1],
        opacity: 1
      }} exit={{
        scale: 0.95,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="size-4"><path d="M5 12l5 5 9-9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></m.svg> : <m.svg key="arrow" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 text-neutral-300" initial={{
        scale: 1
      }} exit={{
        scale: 0.95
      }} transition={{
        duration: 0.2
      }}><path stroke="none" d="M0 0h24v24H0z" fill="none" /><m.path d="M5 12l14 0" initial={{
          strokeDasharray: "50%",
          strokeDashoffset: "50%"
        }} animate={{
          strokeDashoffset: input.value ? 0 : "50%"
        }} transition={{
          duration: 0.3,
          ease: "linear"
        }} /><path d="M13 18l6 -6" /><path d="M13 6l6 6" /></m.svg>}</AnimatePresence>;
    $[3] = input.value;
    $[4] = isSuccess;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t5 || $[9] !== t6) {
    t7 = <button disabled={t1} type="submit" aria-label={t2} className={t5}>{t6}</button>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function PlaceholderText(t0) {
  const $ = _c(6);
  const {
    input,
    placeholders
  } = t0;
  let t1;
  if ($[0] !== input.currentPlaceholder || $[1] !== input.value || $[2] !== placeholders) {
    t1 = !input.value && <m.p initial={{
      opacity: 0
    }} key={`current-placeholder-${input.currentPlaceholder}`} animate={{
      opacity: 1
    }} exit={{
      opacity: 0,
      transition: {
        duration: 0
      }
    }} transition={{
      duration: 0.3,
      ease: "linear"
    }} className="w-[calc(100%-2rem)] truncate pl-4 text-left text-sm font-normal text-neutral-500 sm:pl-4 sm:text-base dark:text-zinc-500">{placeholders[input.currentPlaceholder]}</m.p>;
    $[0] = input.currentPlaceholder;
    $[1] = input.value;
    $[2] = placeholders;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div className="pointer-events-none absolute inset-0 flex items-center rounded-full"><AnimatePresence mode="wait">{t1}</AnimatePresence></div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}